/**
 * scormApi.js — SCORM 1.2 and SCORM 2004 API adapters.
 *
 * Adapted from scorm-runtime-app/frontend/src/scormApi.js for use inside
 * the GoMedulla React frontend (Create React App).
 *
 * Usage:
 *   import { createScorm12Api, createScorm2004Api } from '../utils/scormApi';
 *
 *   const { api } = createScorm12Api({
 *     attemptId,
 *     initialRuntimeData,
 *     scormRuntimeUrl,   // e.g. http://localhost:5050
 *     onCommit: (summary) => { ... },
 *     onLog: (msg, payload) => { ... },
 *   });
 *   window.API = api;
 *   window.parent.API = api;
 */

// Base URL is injected at runtime from the launch response so each instance
// can point to the correct scorm-runtime-app origin.
const makeApiRequest = (baseUrl) => async (path, options = {}) => {
  const response = await fetch(`${baseUrl}${path}`, {
    headers:
      options.body instanceof FormData
        ? options.headers || {}
        : { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });

  const data = await response.json();

  if (!response.ok || data.success === false) {
    const details = data.details ? `\n${JSON.stringify(data.details, null, 2)}` : '';
    throw new Error(`${data.error_msg || 'SCORM API request failed'}${details}`);
  }

  return data.meta ? { data: data.data, meta: data.meta } : data.data;
};

// ─── SCORM 1.2 API ────────────────────────────────────────────────────────────
export const createScorm12Api = ({
  attemptId,
  initialRuntimeData = {},
  scormRuntimeUrl = process.env.REACT_APP_SCORM_RUNTIME_URL || 'http://localhost:5050',
  onCommit,
  onLog,
}) => {
  const apiRequest = makeApiRequest(scormRuntimeUrl);
  let initialized = false;
  let finished = false;
  let lastError = '0';
  const runtimeData = { ...initialRuntimeData };

  const setError = (code) => { lastError = code; };
  const log = (message, payload) => { if (onLog) onLog(message, payload); };

  const api = {
    LMSInitialize: () => {
      if (initialized) { setError('101'); return 'false'; }
      initialized = true;
      finished = false;
      setError('0');
      apiRequest(`/api/attempts/${attemptId}/initialize`, { method: 'POST' }).catch((e) =>
        log('LMSInitialize failed', e.message)
      );
      log('LMSInitialize', null);
      return 'true';
    },

    LMSFinish: () => {
      if (!initialized || finished) { setError('301'); return 'false'; }
      finished = true;
      setError('0');
      api.LMSCommit('');
      apiRequest(`/api/attempts/${attemptId}/finish`, { method: 'POST' }).catch((e) =>
        log('LMSFinish failed', e.message)
      );
      log('LMSFinish', null);
      return 'true';
    },

    LMSGetValue: (key) => {
      if (!initialized) { setError('301'); return ''; }
      setError('0');
      const value = runtimeData[key] ?? '';
      log('LMSGetValue', { key, value });
      return value;
    },

    LMSSetValue: (key, value) => {
      if (!initialized) { setError('301'); return 'false'; }
      runtimeData[key] = value === undefined || value === null ? '' : String(value);
      setError('0');
      log('LMSSetValue', { key, value: runtimeData[key] });
      return 'true';
    },

    LMSCommit: () => {
      if (!initialized) { setError('301'); return 'false'; }
      setError('0');
      const snapshot = { ...runtimeData };
      apiRequest(`/api/attempts/${attemptId}/commit`, {
        method: 'POST',
        body: JSON.stringify({ runtime_data: snapshot }),
      })
        .then((summary) => { if (onCommit) onCommit(summary, snapshot); })
        .catch((e) => { setError('391'); log('LMSCommit failed', e.message); });
      log('LMSCommit', snapshot);
      return 'true';
    },

    LMSGetLastError: () => lastError,
    LMSGetErrorString: (code) => {
      const msgs = { '0': 'No error', '101': 'General exception', '201': 'Invalid argument', '301': 'Not initialized', '391': 'Commit failed', '405': 'Incorrect data type' };
      return msgs[String(code)] || 'Unknown error';
    },
    LMSGetDiagnostic: (code) => `SCORM 1.2 diagnostic: ${code || lastError}`,
  };

  return { api, runtimeData };
};

// ─── SCORM 2004 API ───────────────────────────────────────────────────────────
export const createScorm2004Api = ({
  attemptId,
  initialRuntimeData = {},
  scormRuntimeUrl = process.env.REACT_APP_SCORM_RUNTIME_URL || 'http://localhost:5050',
  onCommit,
  onLog,
}) => {
  const apiRequest = makeApiRequest(scormRuntimeUrl);
  let initialized = false;
  let terminated = false;
  let lastError = '0';
  const runtimeData = { ...initialRuntimeData };

  const setError = (code) => { lastError = code; };
  const log = (message, payload) => { if (onLog) onLog(message, payload); };

  const api = {
    Initialize: () => {
      if (initialized) { setError('103'); return 'false'; }
      initialized = true;
      terminated = false;
      setError('0');
      apiRequest(`/api/attempts/${attemptId}/initialize`, { method: 'POST' }).catch((e) =>
        log('SCORM 2004 Initialize failed', e.message)
      );
      log('SCORM 2004 Initialize', null);
      return 'true';
    },

    Terminate: () => {
      if (!initialized || terminated) { setError('112'); return 'false'; }
      terminated = true;
      setError('0');
      api.Commit('');
      apiRequest(`/api/attempts/${attemptId}/finish`, { method: 'POST' }).catch((e) =>
        log('SCORM 2004 Terminate failed', e.message)
      );
      log('SCORM 2004 Terminate', null);
      return 'true';
    },

    GetValue: (key) => {
      if (!initialized) { setError('122'); return ''; }
      setError('0');
      const value = runtimeData[key] ?? '';
      log('SCORM 2004 GetValue', { key, value });
      return value;
    },

    SetValue: (key, value) => {
      if (!initialized) { setError('132'); return 'false'; }
      runtimeData[key] = value === undefined || value === null ? '' : String(value);
      // Mirror 2004 keys to 1.2 equivalents so our progress tracking works
      if (key === 'cmi.completion_status') runtimeData['cmi.core.lesson_status'] = value === 'completed' ? 'completed' : 'incomplete';
      if (key === 'cmi.success_status' && value === 'passed') runtimeData['cmi.core.lesson_status'] = 'passed';
      if (key === 'cmi.score.raw') runtimeData['cmi.core.score.raw'] = String(value);
      setError('0');
      log('SCORM 2004 SetValue', { key, value: runtimeData[key] });
      return 'true';
    },

    Commit: () => {
      if (!initialized) { setError('142'); return 'false'; }
      setError('0');
      const snapshot = { ...runtimeData };
      apiRequest(`/api/attempts/${attemptId}/commit`, {
        method: 'POST',
        body: JSON.stringify({ runtime_data: snapshot }),
      })
        .then((summary) => { if (onCommit) onCommit(summary, snapshot); })
        .catch((e) => { setError('391'); log('SCORM 2004 Commit failed', e.message); });
      log('SCORM 2004 Commit', snapshot);
      return 'true';
    },

    GetLastError: () => lastError,
    GetErrorString: (code) => {
      const msgs = { '0': 'No error', '103': 'Already initialized', '112': 'Termination before initialization', '122': 'Retrieve before initialization', '132': 'Store before initialization', '142': 'Commit before initialization', '391': 'Commit failed' };
      return msgs[String(code)] || 'Unknown error';
    },
    GetDiagnostic: (code) => `SCORM 2004 diagnostic: ${code || lastError}`,
  };

  return { api, runtimeData };
};
